// The layer over the page: one fixed host element that lets every click through, with a
// ShadowRoot that holds the canvas, a small pointer target over each companion (a press there
// holds the companion, and the page under it never gets that click), the settings opener and the
// settings sheet. Empty page areas keep their clicks, scrolling, text selection and hover.
import { addStyles } from '../ui/styles.js';

const CSS = `
:host { all: initial; }
@media print { :host { display: none !important; } }
[hidden] { display: none !important; }
.stage { position: absolute; left: 0; top: 0; pointer-events: none; }
.targets { position: absolute; inset: 0; pointer-events: none; }
.target {
  position: absolute; pointer-events: auto; touch-action: none; cursor: grab;
  -webkit-tap-highlight-color: transparent; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
}
.target:active { cursor: grabbing; }
.opener {
  position: fixed; left: calc(12px + env(safe-area-inset-left, 0px)); bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  width: 36px; height: 36px; padding: 0; margin: 0; box-sizing: border-box; display: grid; place-items: center;
  border-radius: 50%; border: 1px solid rgba(239, 230, 220, .14); background: rgba(30, 26, 23, .7);
  box-shadow: 0 2px 10px rgba(0, 0, 0, .18); cursor: pointer; pointer-events: auto;
  transition: background .15s, border-color .15s; -webkit-tap-highlight-color: transparent;
}
.opener:hover, .opener:focus-visible { background: rgba(30, 26, 23, .92); border-color: rgba(239, 230, 220, .3); }
.opener:focus-visible { outline: 2px solid #d77757; outline-offset: 2px; }
.opener canvas { width: 30px; height: 30px; display: block; }
@media (prefers-reduced-motion: reduce) { .opener { transition: none; } }
`;

const OPENER = 30;                                  // the opener's portrait, in CSS pixels

const el = (tag, className) => Object.assign(document.createElement(tag), className ? { className } : {});

export function createOverlay({ nonce, button = true, nameOf = id => id, onOpen = () => {} } = {}) {
  const host = document.createElement('companions-overlay');
  // Inline CSSOM styles win over anything the page says about this element (and a CSP allows them).
  for (const [name, value] of [['position', 'fixed'], ['inset', '0'], ['z-index', '2147483000'], ['pointer-events', 'none'],
    ['margin', '0'], ['padding', '0'], ['border', '0'], ['background', 'none'], ['transform', 'none'], ['filter', 'none']]) {
    host.style.setProperty(name, value, 'important');
  }
  const root = host.attachShadow({ mode: 'open' });
  const removeStyles = addStyles(root, CSS, { nonce });
  const canvas = el('canvas', 'stage');
  canvas.setAttribute('aria-hidden', 'true');
  const targets = el('div', 'targets');
  root.append(canvas, targets);

  let opener = null, portrait = null;
  if (button) {
    opener = el('button', 'opener');
    opener.type = 'button';
    opener.title = 'Companion settings';
    opener.setAttribute('aria-label', 'Companion settings');
    opener.setAttribute('aria-haspopup', 'dialog');
    const dpr = globalThis.devicePixelRatio || 1;
    portrait = el('canvas');
    portrait.width = portrait.height = Math.round(OPENER * dpr);
    portrait.setAttribute('aria-hidden', 'true');
    opener.append(portrait);
    opener.addEventListener('click', () => onOpen(opener));
    root.append(opener);
  }
  document.documentElement.append(host);

  // One target per companion, from the runtime's hit regions (logical canvas pixels = client pixels).
  // They carry the companion's name for screen readers but stay out of the tab order: the
  // settings opener is the keyboard's way in.
  const byId = new Map();
  function setRegions(regions) {
    const seen = new Set();
    for (const { id, x, y, width, height } of regions) {
      seen.add(id);
      let target = byId.get(id);
      if (!target) {
        target = el('div', 'target');
        target.setAttribute('role', 'img');
        target.setAttribute('aria-label', nameOf(id));
        targets.append(target);
        byId.set(id, target);
      }
      const s = target.style;
      s.left = x + 'px'; s.top = y + 'px'; s.width = width + 'px'; s.height = height + 'px';
    }
    for (const [id, target] of byId) if (!seen.has(id)) { target.remove(); byId.delete(id); }
  }

  // Where the corner the opener takes ends, in CSS pixels from the left (0 without one): the floor
  // starts right of it. The opener already keeps out of the safe area.
  const corner = () => (opener ? opener.getBoundingClientRect().right + 8 : 0);

  return {
    host, root, canvas, opener, portrait, setRegions, corner,
    setHidden(on) { canvas.hidden = targets.hidden = on; },
    destroy() { setRegions([]); removeStyles(); host.remove(); },
  };
}
